import React, { useEffect, useMemo, useRef, useState } from "react";
import { Checkbox } from "antd";
import { StatChartV2 } from "../StatChartV2/StatChartV2.jsx";
import BarChartHighcharts from "../Charts/BarChart";
import { enhanceOptionsWithOptionalFeatures } from "../../mappers/optionEnhancers.js";

// Generic overlay wrapper for StatChartV2 and BarChartHighcharts
// props:
// - chartType: 'stat' | 'bar'
// - For 'stat', forward props to StatChartV2
// - For 'bar', expect `options` builder props and forward to BarChartHighcharts
// - optionalFeatures: { [propName: string]: string } mapping prop -> label
export default function OptionalFeaturesForCharts({ chartType = 'stat', optionalFeatures = {}, optionsBuilder = null, ...props }) {
  const featureKeys = useMemo(() => Object.keys(optionalFeatures || {}), [optionalFeatures]);

  const initFromProps = () => {
    const initial = {};
    featureKeys.forEach((k) => {
      initial[k] = Boolean(props[k]);
    });
    return initial;
  };

  const [featureStates, setFeatureStates] = useState(initFromProps);
  const [hasData, setHasData] = useState(true);
  const containerRef = useRef(null);

  useEffect(() => {
    const next = initFromProps();
    const keysA = Object.keys(featureStates);
    const keysB = Object.keys(next);
    let changed = keysA.length !== keysB.length;
    if (!changed) {
      for (let i = 0; i < keysA.length; i++) {
        const k = keysA[i];
        if (featureStates[k] !== next[k]) { changed = true; break; }
      }
    }
    if (changed) {
      setFeatureStates(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionalFeatures]);

  const forwardedProps = useMemo(() => ({
    ...props,
    ...featureStates
  }), [props, featureStates]);

  // Hide checkboxes when an AntD Empty placeholder is rendered
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const check = () => {
      const empty = node.querySelector('.ant-empty');
      setHasData(!empty);
    };
    check();
    const observer = new MutationObserver(() => check());
    observer.observe(node, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%", paddingTop: 28 }}>
      {chartType === 'stat' ? (
        <StatChartV2
          {...forwardedProps}
          enhancer={(opts) => enhanceOptionsWithOptionalFeatures({ ...(opts || {}) }, {
            // Always show average line on StatChartV2 (testing/forceplate)
            showAvgPlotline: true,
            showTrendlines: !!featureStates?.showTrendlines
          })}
        />
      ) : (
        <BarChartHighcharts
          {...props}
          options={(() => {
            const base = optionsBuilder ? optionsBuilder(featureStates) : props.options;
            return enhanceOptionsWithOptionalFeatures({ ...(base || {}) }, {
              showAvgPlotline: !!featureStates?.showAvgPlotline || !!featureStates?.showAvgRefLine,
              showTrendlines: !!featureStates?.showTrendlines
            });
          })()}
        />
      )}
      {hasData && featureKeys.length > 0 ? (
        <div
          style={{
            position: "absolute",
            top: 30,
            left: 10,
            background: "rgba(255,255,255,0.85)",
            borderRadius: 4,
            padding: "6px 8px",
            display: "flex",
            flexDirection: "column",
            gap: 4,
            zIndex: 10
          }}
        >
          {featureKeys.map((key) => (
            <Checkbox
              key={key}
              checked={!!featureStates[key]}
              onChange={(e) => setFeatureStates((prev) => ({ ...prev, [key]: e.target.checked }))}
              style={{ fontSize: 12, color: "#666", marginLeft: 0 }}
            >
              {optionalFeatures[key] || key}
            </Checkbox>
          ))}
        </div>
      ) : null}
    </div>
  );
}


